export const splitArray = <T>(array: T[], filterFn: (item: T, index: number) => boolean) => {
  const matched: T[] = []
  const unmatched: T[] = []
  for (const [i, item] of array.entries()) {
    ;(filterFn(item, i) ? matched : unmatched).push(item)
  }
  return [matched, unmatched] as [T[], T[]]
}

type TransformFn<T> = (pair: [T[], T[]]) => T[]
export const partitionArrayAndMerge = <T>(array: T[], filterFn: (item: T) => boolean, transform?: TransformFn<T>) => {
  const splitResult = splitArray(array, filterFn)
  return transform ? transform(splitResult) : [...splitResult[0], ...splitResult[1]]
}

export const toChunks = <T>(array: T[], chunkSize: number) => {
  const chunks: T[][] = []
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize))
  }
  return chunks
}

export const unique = <T>(...arrays: T[][]) => {
  // eslint-disable-next-line immutablejs/no-native-map-set
  const set = new window.Set<T>(([] as T[]).concat(...arrays))
  return Array.from(set)
}

/**
 * This is for array of arrays, to transpose rows and columns
 * Example:
 * Input: [[1,2,3],[4,5,6],[7,8,9]]
 * Output: [[1,4,7],[2,5,8],[3,6,9]]
 */
export const transpose = <T>(array: T[][]): T[][] => {
  if (0 === array.length) return []
  return array[0].map((_, colIndex) => array.map(row => row[colIndex]))
}

type KeyExtractor<T, K extends PropertyKey> = ((item: T) => K) | (keyof T & K)

type ValueExtractor<T, V> = ((item: T) => V) | undefined

export function toKeyedRecord<T, K extends PropertyKey, V = T>(
  items: T[] | Iterable<T>,
  keyExtractor: KeyExtractor<T, K>,
  valueExtractor?: ValueExtractor<T, V>
): Record<K, V> {
  return (Array.isArray(items) ? items : Array.from(items)).reduce(
    (acc, item) => {
      const key = 'function' === typeof keyExtractor ? keyExtractor(item) : (item[keyExtractor] as K)
      const value = 'function' === typeof valueExtractor ? valueExtractor(item) : (item as unknown as V)
      acc[key] = value
      return acc
    },
    {} as Record<K, V>
  )
}

export function getIdentityArrayOfLength(n: number): number[] {
  return Array.from({ length: n }, (_, i) => i)
}
export function isIdentityVector(array: number[]): boolean {
  return Array.isArray(array) && array.every((value, index) => value === index)
}
export function isIdentityVectorOfLength(length: number, array: number[]) {
  return array?.length === length && isIdentityVector(array)
}

export function mapSourceByPositions<T>(source: T[], positions: number[]): T[] {
  return positions.map(pos => source[pos])
}

export function* iterateSourceByPositions<T>(source: T[], positions: number[]): Generator<T> {
  // for (let i = 0; i < source.length; i++) {
  //   yield i >= positions.length ? source[i] : source[positions[i]]
  // }
  for (const pos of positions) {
    if (pos < 0) {
      continue
    }
    if (pos < source.length) {
      yield source[pos]
    }
  }
}

export function getNewPositionsForModifiedSource<T>(
  oldSource: T[],
  newSource: T[],
  comparer?: (a: T, b: T) => boolean
): number[] {
  if (!comparer) {
    const oldSourceIndex = new Map<T, number>()
    ;(oldSource || []).forEach((item, index) => {
      oldSourceIndex.set(item, index)
    })
    return (newSource || []).map((item, newSourceIndex) => oldSourceIndex.get(item) ?? newSourceIndex)
  }

  return (newSource || []).map((item, _newSourceIndex) => {
    const oldSourceIndex = (oldSource || []).findIndex(oldItem => comparer(item, oldItem))
    return oldSourceIndex // oldSourceIndex >= 0 ? oldSourceIndex : newSourceIndex
  })
}

export function invertPositions(positions: number[]): number[] {
  const inverse: number[] = Array.from({ length: positions.length })
  positions.forEach((pos, i) => {
    inverse[pos] = i
  })
  return inverse
}
